import React, { useEffect, useState } from 'react';
import { Add } from '@mui/icons-material';
import {
    REmptyText,
    RErrorText,
    RModal,
    RPillButton,
    RRow,
    RStack,
    RToast,
} from 'recruit/components';
import { AddPaymentMethod } from './AddPaymentMethod';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { PaymentMethodRow } from './PaymentMethodRow';
import {
    deletePaymentMethod,
    getPaymentMethods,
    setDefaultPaymentMethod,
    setRecruitDefaultPaymentMethod,
} from './api';
import { PaymentMethod, isBankAccount } from './types';

type PaymentTypeKind = 'card' | 'bank';

type PaymentProduct = 'recruit';

interface ManagePaymentMethodsProps {
    product?: PaymentProduct;
    paymentTypesAllowed?: PaymentTypeKind[];
    showRecruitDefault?: boolean;
    showSubsDefault?: boolean;
    onMethodsChange?: (methods: PaymentMethod[]) => void;
    onRecruitDefaultSet?: () => void;
}

export function ManagePaymentMethods({
    product,
    paymentTypesAllowed,
    showRecruitDefault = true,
    showSubsDefault,
    onMethodsChange,
    onRecruitDefaultSet,
}: ManagePaymentMethodsProps) {
    const isRecruit = product === 'recruit';
    const resolvedPaymentTypes = paymentTypesAllowed ?? (isRecruit ? ['card'] : ['card', 'bank']);
    const resolvedShowSubsDefault = showSubsDefault ?? !isRecruit;
    const recruitDefaultLabel = isRecruit ? 'Default' : 'Recruit Default';
    const setRecruitDefaultLabel = isRecruit ? 'Set as default' : 'Set Recruit Default';
    const [methods, setMethods] = useState<PaymentMethod[] | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [showAdd, setShowAdd] = useState(false);
    const [toast, setToast] = useState<string | null>(null);
    const [pendingDelete, setPendingDelete] = useState<PaymentMethod | null>(null);
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);

    function refetch() {
        getPaymentMethods()
            .then((fetched) => {
                setMethods(fetched);
                onMethodsChange?.(fetched);
            })
            .catch(() => setLoadError('Could not load payment methods.'));
    }

    useEffect(refetch, []);

    useEffect(() => {
        if (!toast) return undefined;
        const t = window.setTimeout(() => setToast(null), 4000);
        return () => window.clearTimeout(t);
    }, [toast]);

    async function handleSetSubsDefault(method: PaymentMethod) {
        try {
            await setDefaultPaymentMethod(method.id);
            refetch();
            setToast('Default payment method updated.');
        } catch {
            setToast('Could not update default. Please try again.');
        }
    }

    async function handleSetRecruitDefault(method: PaymentMethod) {
        try {
            await setRecruitDefaultPaymentMethod(method.id);
            refetch();
            setToast(isRecruit ? 'Default payment method updated.' : 'Recruit default updated.');
            onRecruitDefaultSet?.();
        } catch {
            setToast('Could not update default. Please try again.');
        }
    }

    function startDelete(method: PaymentMethod) {
        if (method.is_default || method.is_recruit_default) {
            setToast('Set another payment method as default before deleting this one.');
            return;
        }
        setPendingDelete(method);
    }

    async function confirmDelete() {
        if (!pendingDelete) return;
        setDeleteSubmitting(true);
        try {
            await deletePaymentMethod(pendingDelete.id);
            setPendingDelete(null);
            refetch();
            setToast('Payment method removed.');
        } catch (err: unknown) {
            const detail =
                err && typeof err === 'object' && 'detail' in err && typeof (err as { detail: unknown }).detail === 'string'
                    ? (err as { detail: string }).detail
                    : 'Could not delete payment method.';
            setToast(detail);
        } finally {
            setDeleteSubmitting(false);
        }
    }

    if (loadError) {
        return <RErrorText>{loadError}</RErrorText>;
    }

    if (methods === null) {
        return <REmptyText>Loading…</REmptyText>;
    }

    const visibleMethods = isRecruit ? methods.filter((method) => !isBankAccount(method)) : methods;

    return (
        <RStack gap="md">
            {visibleMethods.map((method) => (
                <PaymentMethodRow
                    key={method.id}
                    method={method}
                    showSubsDefault={resolvedShowSubsDefault}
                    showRecruitDefault={showRecruitDefault}
                    recruitDefaultLabel={recruitDefaultLabel}
                    setRecruitDefaultLabel={setRecruitDefaultLabel}
                    onSetSubsDefault={() => handleSetSubsDefault(method)}
                    onSetRecruitDefault={() => handleSetRecruitDefault(method)}
                    onDelete={() => startDelete(method)}
                />
            ))}

            <RRow>
                <RPillButton
                    variant="secondary"
                    onClick={() => setShowAdd(true)}
                    icon={<Add sx={{ fontSize: 16 }} />}
                >
                    Add Payment Method
                </RPillButton>
            </RRow>

            {showAdd && (
                <RModal title="Add Payment Method" onClose={() => setShowAdd(false)} width={520}>
                    <AddPaymentMethod
                        allowedKinds={resolvedPaymentTypes}
                        onSuccess={(method) => {
                            setShowAdd(false);
                            refetch();
                            setToast('Payment method added.');
                            if (method.is_recruit_default) onRecruitDefaultSet?.();
                        }}
                        onCancel={() => setShowAdd(false)}
                    />
                </RModal>
            )}

            {pendingDelete && (
                <DeleteConfirmModal
                    method={pendingDelete}
                    submitting={deleteSubmitting}
                    onCancel={() => setPendingDelete(null)}
                    onConfirm={confirmDelete}
                />
            )}

            <RToast message={toast} />
        </RStack>
    );
}
